import { useState, useEffect } from 'react';
import { BarChart3, Download, RefreshCw } from 'lucide-react';
import { adminAxios } from './AdminLayout';

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCampaignDiag() {
  var [campaigns, setCampaigns] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selectedCampaign, setSelectedCampaign] = useState(null);
  var [recipients, setRecipients] = useState(null);
  var [recipientFilter, setRecipientFilter] = useState(null);

  function fetchCampaigns() {
    adminAxios().get('/api/admin/campaigns').then(function(r) { setCampaigns(r.data); setLoading(false); });
  }
  useEffect(function() {
    fetchCampaigns();
    // Auto-refresh every 5 seconds for live monitoring
    var interval = setInterval(fetchCampaigns, 5000);
    return function() { clearInterval(interval); };
  }, []);

  async function viewRecipients(cid, status) {
    setSelectedCampaign(cid);
    setRecipientFilter(status);
    var url = '/api/admin/campaigns/' + cid + '/recipients';
    if (status) url += '?status=' + status;
    var r = await adminAxios().get(url);
    setRecipients(r.data.recipients);
  }

  function exportFailed(cid) {
    var token = localStorage.getItem('admin_token');
    fetch(BACKEND_URL + '/api/admin/campaigns/' + cid + '/failed-csv', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(r) { return r.blob(); })
      .then(function(blob) { var a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'failed_recipients.csv'; a.click(); });
  }

  var statusColors = { ready: 'bg-slate-100 text-slate-600', sending: 'bg-blue-100 text-blue-700', completed: 'bg-green-100 text-green-700', partial: 'bg-amber-100 text-amber-700', failed: 'bg-red-100 text-red-700', paused: 'bg-yellow-100 text-yellow-700' };

  return (
    <div data-testid="admin-campaign-diag">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-outfit text-2xl font-bold text-slate-900">Campaign Diagnostics</h2>
        <button onClick={fetchCampaigns} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 font-manrope text-sm"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      {loading ? <div className="font-manrope text-slate-500">Loading...</div> :
       campaigns.length === 0 ? <div className="text-center py-12 bg-white rounded-2xl border border-slate-100"><BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="font-manrope text-slate-500">No campaigns yet</p></div> :
       <div className="space-y-4">
        {campaigns.map(function(c) {
          var pct = c.total_recipients > 0 ? Math.round(c.sent_count / c.total_recipients * 100) : 0;
          return (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className={'px-2 py-0.5 rounded-full text-xs font-semibold mr-2 ' + (statusColors[c.status] || 'bg-slate-100 text-slate-600')}>{c.status}</span>
                  <span className="font-manrope text-xs text-slate-400">{c.mode} mode · batch {c.batch_size}</span>
                </div>
                <span className="font-manrope text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
              </div>

              <div className="grid grid-cols-5 gap-3 mb-3">
                <div className="text-center p-2 bg-slate-50 rounded-lg"><p className="font-space text-lg font-bold text-slate-900">{c.total_recipients}</p><p className="font-manrope text-[9px] text-slate-500">Total</p></div>
                <div className="text-center p-2 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100" onClick={function(){viewRecipients(c.id,'sent');}}><p className="font-space text-lg font-bold text-green-600">{c.sent_count}</p><p className="font-manrope text-[9px] text-green-600">Sent</p></div>
                <div className="text-center p-2 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100" onClick={function(){viewRecipients(c.id,'failed');}}><p className="font-space text-lg font-bold text-red-500">{c.failed_count}</p><p className="font-manrope text-[9px] text-red-500">Failed</p></div>
                <div className="text-center p-2 bg-slate-50 rounded-lg"><p className="font-space text-lg font-bold text-slate-400">{c.skipped_count || 0}</p><p className="font-manrope text-[9px] text-slate-400">Skipped</p></div>
                <div className="text-center p-2 bg-blue-50 rounded-lg"><p className="font-space text-lg font-bold text-blue-500">{pct}%</p><p className="font-manrope text-[9px] text-blue-500">Delivered</p></div>
              </div>

              <div className="w-full bg-slate-100 rounded-full h-2 mb-3"><div className="bg-green-500 rounded-full h-2" style={{width: pct + '%'}}></div></div>

              {/* Live monitoring */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-[10px] font-manrope">
                {c.worker_active && <span className="px-2 py-1 bg-green-50 text-green-700 rounded">Worker: ACTIVE</span>}
                {c.live_status && <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded truncate" title={c.live_status}>{c.live_status}</span>}
                {c.avg_send_duration > 0 && <span className="px-2 py-1 bg-slate-50 text-slate-600 rounded">Avg: {c.avg_send_duration}s/email</span>}
                {c.completion_pct > 0 && <span className="px-2 py-1 bg-green-50 text-green-700 rounded">{c.completion_pct}% delivered</span>}
                {c.last_success_at && <span className="px-2 py-1 bg-slate-50 text-slate-500 rounded">Last OK: {new Date(c.last_success_at).toLocaleTimeString()}</span>}
                {c.last_failure_at && <span className="px-2 py-1 bg-red-50 text-red-500 rounded">Last fail: {new Date(c.last_failure_at).toLocaleTimeString()}</span>}
                {c.pause_reason && <span className="px-2 py-1 bg-red-100 text-red-700 rounded col-span-2">{c.pause_reason}</span>}
              </div>

              {/* Sample message IDs */}
              {(c.sample_message_ids || []).length > 0 && (
                <div className="mb-3 text-[10px] font-mono text-slate-400">
                  <span className="font-sans font-semibold text-slate-600">Brevo IDs: </span>
                  {c.sample_message_ids.map(function(m, i) { return <span key={i} className="mr-3">{m.email}: {(m.id || '').substring(0, 20)}...</span>; })}
                </div>
              )}

              {/* Batch log */}
              {(c.batch_log || []).length > 0 && (
                <div className="max-h-24 overflow-y-auto mb-3 space-y-0.5">
                  {c.batch_log.map(function(bl, i) { return <div key={i} className="flex items-center justify-between px-2 py-1 rounded text-[10px] font-manrope bg-slate-50"><span>Batch {bl.num}</span><span className={bl.sent > 0 ? 'text-green-600' : 'text-red-500'}>{bl.sent} sent{bl.failed > 0 ? ', ' + bl.failed + ' failed' : ''}</span><span className="text-slate-400">{bl.timestamp ? new Date(bl.timestamp).toLocaleTimeString() : ''}</span></div>; })}
                </div>
              )}

              <div className="flex gap-2">
                <button onClick={function(){viewRecipients(c.id, null);}} className="px-3 py-1.5 rounded-lg border border-slate-200 font-manrope text-xs">All Recipients</button>
                <button onClick={function(){viewRecipients(c.id, 'failed');}} className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 font-manrope text-xs">Failed Only</button>
                <button onClick={function(){exportFailed(c.id);}} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 font-manrope text-xs"><Download className="w-3 h-3" /> Export Failed CSV</button>
              </div>
            </div>
          );
        })}
       </div>
      }

      {/* Recipient detail panel */}
      {recipients && (
        <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit text-lg font-semibold text-slate-900">Recipients {recipientFilter ? '(' + recipientFilter + ')' : '(all)'} — {recipients.length}</h3>
            <button onClick={function(){setRecipients(null);}} className="font-manrope text-xs text-slate-500 hover:underline">Close</button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50">
                <tr><th className="text-left px-2 py-1.5 font-manrope">Email</th><th className="text-left px-2 py-1.5 font-manrope">Name</th><th className="text-left px-2 py-1.5 font-manrope">Status</th><th className="text-left px-2 py-1.5 font-manrope">Attempts</th><th className="text-left px-2 py-1.5 font-manrope">Brevo Response</th><th className="text-left px-2 py-1.5 font-manrope">Failure Reason</th></tr>
              </thead>
              <tbody>
                {recipients.map(function(r, i) {
                  var sc = r.status === 'sent' ? 'text-green-600' : r.status === 'failed' ? 'text-red-500' : 'text-slate-500';
                  return <tr key={i} className="border-b border-slate-50">
                    <td className="px-2 py-1.5 font-manrope">{r.email}</td>
                    <td className="px-2 py-1.5 font-manrope text-slate-500">{r.full_name || '-'}</td>
                    <td className={'px-2 py-1.5 font-manrope font-semibold ' + sc}>{r.status}</td>
                    <td className="px-2 py-1.5 font-manrope">{r.attempts || 0}</td>
                    <td className="px-2 py-1.5 font-manrope text-slate-500 max-w-[150px] truncate">{r.brevo_response || '-'}</td>
                    <td className="px-2 py-1.5 font-manrope text-red-500 max-w-[200px] truncate">{r.failure_reason || '-'}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
